"use client";

// The `ctx` a vBlock is handed, instead of asking "where am I".
//
// This is the Context row of the widget contract in the research pack
// (08_VTOOL_COMPOSER_MODEL.md §4): a widget never queries who it is or where
// it stands — the host injects it. The wrapper already computes every one of
// these values (blockId, mode, size, page, fullscreen) and already hands them
// down, but only through the render-prop form of `children`:
//
//     <VBlockShell ...>{({ mode, size, page }) => <Thing … />}</VBlockShell>
//
// A block registered in the BlockRegistry cannot use that form. The registry's
// contract is that a registered component takes NO props (see registerStatCard
// and registerVBlockFields) — so the render-prop values, which exist, could not
// reach the nine components that were just registered. This context is the
// second door onto the same values, and it is deliberately the same object the
// render prop receives, not a parallel copy that can drift.
//
// What is NOT here, and why:
//   • the record — `useBlockRecord()` answers that, and a record outlives any
//     one wrapper (NoteMeta hosts nine blocks over one note).
//   • locale and direction — `useSettings()` already owns them. A second copy
//     inside ctx would be one truth in two places.
//   • principal and permissions — the §4 contract lists them, and nothing in
//     the wrapper can honestly fill them yet. A field that is always undefined
//     teaches the next reader that the contract is optional.

import { createContext, useContext, useMemo } from "react";
import type { VBlockMode, VBlockSize } from "./vBlock.types";

export interface VBlockCtx {
  /** Instance id of this wrapper — the §4 `widgetInstanceId`. */
  blockId: string;
  /** Size bucket the wrapper resolved for the current box. */
  mode: VBlockMode;
  /** Actual pixel box, already clamped by min/max. */
  size: VBlockSize;
  /** Which face of a flippable block is showing. */
  page: "front" | "back" | "both";
  /** True while the block is rendered into the fullscreen portal. */
  isFullscreen: boolean;
}

const Ctx = createContext<VBlockCtx | null>(null);

export function VBlockCtxProvider({
  value,
  children,
}: {
  value: VBlockCtx;
  children: React.ReactNode;
}) {
  // Memoized on the five primitives, not on the object: the wrapper rebuilds
  // this object on every resize tick, and an unmemoized provider would rerender
  // every descendant on each one.
  const memo = useMemo(
    () => value,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value.blockId, value.mode, value.size.width, value.size.height, value.page, value.isFullscreen],
  );
  return <Ctx.Provider value={memo}>{children}</Ctx.Provider>;
}

/**
 * The wrapper this block stands in, or null when it stands bare.
 *
 * Null is a legitimate answer, not an error: a block rendered straight into a
 * grid slot with no VBlockShell around it is a normal case today, and it should
 * render at its natural size rather than crash asking for a mode.
 */
export function useVBlockCtx(): VBlockCtx | null {
  return useContext(Ctx);
}

/** The size bucket, with a sane answer when there is no wrapper. */
export function useVBlockMode(): VBlockMode {
  return useContext(Ctx)?.mode ?? "standard";
}
